import './AgentChat.scss';
import cn from 'classnames';
import { format } from 'date-fns';
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactMarkdown from 'react-markdown';
import { Form } from 'react-bootstrap';
import { Radio } from '@Components/FormCheck';
import {
  MdArrowUpward,
  MdAttachFile,
  MdBuild,
  MdClose,
  MdInsertDriveFile,
  MdOutlineAutoAwesome,
  MdOutlineFileDownload,
  MdOutlineThumbDownAlt,
  MdOutlineThumbUpAlt,
  MdShare,
  MdStop,
  MdThumbDownAlt,
  MdThumbUpAlt
} from 'react-icons/md';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { CopyButton } from '@Components/CopyButton';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';
import {
  AgentArtifactSegment,
  AgentNoteSegment,
  AgentResponseSegment,
  AgentTextSegment
} from '../../utils/agent-client';
import {
  CSV_ATTACHMENT_ACCEPT,
  Feedback,
  FeedbackType,
  HANDOFF_TOOL_NAME,
  NEGATIVE_FEEDBACK_OPTIONS,
  PRIVACY_POLICY_URL
} from './agent-chat-constants';
import { AGENT_CHAT_MARKDOWN_COMPONENTS } from './AgentChatMarkdownLink';
import {
  AGENT_CHAT_REMARK_PLUGINS,
  artifactSegmentsLast,
  formatFileSize,
  formatToolArgs,
  getCsvAttachmentError,
  groupToolCalls,
  humanizeToolName,
  readCsvAttachment
} from './agent-chat-utils';
import {
  AgentMessage,
  ChatAttachment,
  FeedbackSubmission,
  MessageRole,
  PendingAttachment
} from './agent-chat-types';

interface FeedbackState {
  feedback: FeedbackType | null;
  reason: string | null;
}

interface AgentChatProps {
  messages: AgentMessage[];
  submitting: boolean;
  draftMessage: string;
  setDraftMessage: Dispatch<SetStateAction<string>>;
  onSubmit: (prompt: string, attachment?: PendingAttachment) => void;
  // When set, the send button becomes a Stop control while submitting and Escape triggers it.
  onStop?: () => void;
  // True between Stop and the stream ending; the Stop control shows pending and ignores Escape.
  stopping?: boolean;
  emptyState?: ReactNode;
  suggestedPrompts?: string[];
  disableFeedback?: boolean;
  onFeedback?: (submission: FeedbackSubmission) => void;
  // When false, the prompt textarea will not auto-focus on mount — set to the
  // host's visibility flag when this is rendered inside a hidden container
  // (e.g. a FloatingPanel that starts closed) so it doesn't steal page focus.
  visible?: boolean;
  placeholder?: string;
  // A single tip shown just above the prompt box in an empty chat; clicking it fills the prompt.
  promptTip?: string;
  onPromptTipShown?: (tip: string) => void;
  messageLabel?: (agent: string | undefined) => string | undefined;
  // Shows an attach button that sends one CSV with the message. Off by default; only hosts whose
  // agent accepts attachments turn it on.
  allowCsvAttachment?: boolean;
  // Changing it clears the prompt box's pending attachment. Hosts pass the session id, so a file
  // picked in one chat never rides along into a new one.
  composerKey?: string;
}

export const AgentChat = ({
  messages,
  submitting,
  draftMessage,
  setDraftMessage,
  onSubmit,
  onStop,
  stopping,
  emptyState,
  suggestedPrompts,
  disableFeedback = true,
  onFeedback,
  visible = true,
  placeholder,
  promptTip,
  onPromptTipShown,
  messageLabel,
  allowCsvAttachment = false,
  composerKey
}: AgentChatProps) => {
  const [feedbackByMessage, setFeedbackByMessage] = useState<Record<string, FeedbackState>>({});
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const shownTip = promptTip && messages.length === 0 ? promptTip : undefined;
  useEffect(() => {
    if (shownTip) {
      onPromptTipShown?.(shownTip);
    }
  }, [shownTip, onPromptTipShown]);

  const submitFeedback = (submission: FeedbackSubmission) => {
    setFeedbackByMessage(prev => ({
      ...prev,
      [submission.messageId]: { feedback: submission.feedback, reason: submission.reason }
    }));
    onFeedback?.(submission);
  };

  return (
    <div className='agent-chat'>
      <div className='chat-area'>
        {messages.length === 0 ? (
          <div className='empty-state'>
            {emptyState}
            {suggestedPrompts && suggestedPrompts.length > 0 && (
              <SuggestedPrompts
                prompts={suggestedPrompts}
                onSelect={prompt => {
                  setDraftMessage(prompt);
                  promptInputRef.current?.focus();
                }}
              />
            )}
          </div>
        ) : (
          <MessageList
            messages={messages}
            disableFeedback={disableFeedback}
            feedbackByMessage={feedbackByMessage}
            submitFeedback={submitFeedback}
            messageLabel={messageLabel}
          />
        )}
      </div>
      {shownTip && (
        <PromptTip
          tip={shownTip}
          onSelect={() => {
            setDraftMessage(shownTip);
            promptInputRef.current?.focus();
          }}
        />
      )}
      <PromptInput
        key={composerKey}
        inputRef={promptInputRef}
        sendPrompt={onSubmit}
        onStop={onStop}
        stopping={stopping}
        submitting={submitting}
        draftMessage={draftMessage}
        setDraftMessage={setDraftMessage}
        autoFocus={visible}
        placeholder={placeholder}
        allowCsvAttachment={allowCsvAttachment}
      />
      <PrivacyFooter messages={messages} />
    </div>
  );
};

interface MessageListProps {
  messages: AgentMessage[];
  disableFeedback: boolean;
  feedbackByMessage: Record<string, FeedbackState>;
  submitFeedback: (submission: FeedbackSubmission) => void;
  messageLabel?: (agent: string | undefined) => string | undefined;
}

const MessageList = ({
  messages,
  disableFeedback,
  feedbackByMessage,
  submitFeedback,
  messageLabel
}: MessageListProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // useLayoutEffect so the scroll starts from the post-commit position —
  // with useEffect the new content paints at the old scroll first, then
  // the smooth-scroll catches up. Re-runs on every assistant segment
  // update too, not just message-count, so streaming text stays pinned
  // to the bottom.
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  return (
    <div className='message-list'>
      {messages.map((message, index) => (
        <ChatMessage
          key={message.id}
          message={message}
          prevTimestamp={index === 0 ? null : messages[index - 1].timestamp}
          disableFeedback={disableFeedback}
          feedbackState={feedbackByMessage[message.id]}
          submitFeedback={submitFeedback}
          label={message.role === MessageRole.ASSISTANT ? messageLabel?.(message.agent) : undefined}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  );
};

interface ChatMessageProps {
  message: AgentMessage;
  prevTimestamp: number | null;
  disableFeedback: boolean;
  feedbackState: FeedbackState | undefined;
  submitFeedback: (submission: FeedbackSubmission) => void;
  label?: string;
}

// The handoff tool call is acted on by the client, never shown; its outcome is a note segment.
const isVisibleSegment = (segment: AgentResponseSegment): boolean =>
  !(segment.type === 'tool_call' && segment.name === HANDOFF_TOOL_NAME);

const ChatMessage = ({
  message,
  prevTimestamp,
  disableFeedback,
  feedbackState,
  submitFeedback,
  label
}: ChatMessageProps) => {
  const { role, timestamp } = message;
  const isInternal = useAppSelector(UserRedux.isInternalSelector);

  const thisDate = useMemo(() => new Date(timestamp), [timestamp]);
  const prettyTime = useMemo(() => format(thisDate, 'h:mma'), [thisDate]);
  const prettyDate = useMemo(() => {
    const comparison = prevTimestamp ? new Date(prevTimestamp) : new Date();
    if (
      comparison.getDate() === thisDate.getDate() &&
      comparison.getMonth() === thisDate.getMonth() &&
      comparison.getFullYear() === thisDate.getFullYear()
    ) {
      return null;
    }
    return format(thisDate, 'MMMM d, y');
  }, [prevTimestamp, thisDate]);

  const copyText =
    message.role === MessageRole.USER
      ? message.content
      : message.segments
          .filter((s): s is AgentTextSegment => s.type === 'text')
          .map(s => s.content)
          .join('\n\n');

  const showFeedback = !disableFeedback && message.role === MessageRole.ASSISTANT;
  // Fall back to feedback restored from a prior session (via hydration) until the
  // user overrides it locally this session. Check presence with a ternary rather than
  // `??` — `feedbackState.feedback`/`.reason` is legitimately `null` once the user clears
  // their vote, and `null ?? persistedFeedback` would incorrectly fall through to it.
  const persistedFeedback =
    message.role === MessageRole.ASSISTANT ? (message.persistedFeedback ?? null) : null;
  const persistedReason =
    message.role === MessageRole.ASSISTANT ? (message.persistedFeedbackReason ?? null) : null;
  const currentFeedback = feedbackState ? feedbackState.feedback : persistedFeedback;
  const currentReason = feedbackState ? feedbackState.reason : persistedReason;
  const onFeedbackClick = (type: FeedbackType) => {
    const next = currentFeedback === type ? null : type;
    submitFeedback({
      messageId: message.id,
      feedback: next,
      reason: next === Feedback.NEGATIVE ? currentReason : null
    });
  };
  const onReasonSubmit = (reason: string) => {
    submitFeedback({ messageId: message.id, feedback: Feedback.NEGATIVE, reason });
  };

  const segments =
    message.role === MessageRole.ASSISTANT ? message.segments.filter(isVisibleSegment) : [];

  // Don't render an empty bubble while the first token is in-flight.
  if (message.role === MessageRole.ASSISTANT && segments.length === 0) {
    return null;
  }

  // Nothing to show but a system note (a handoff with no other content): one line, no bubble and no
  // feedback buttons.
  const notes = segments.filter((s): s is AgentNoteSegment => s.type === 'note');
  const isNoteOnly = notes.length > 0 && segments.every(s => s.type === 'note');
  if (isNoteOnly) {
    return (
      <>
        {prettyDate && (
          <div className='message-date'>
            {prettyDate} • {prettyTime}
          </div>
        )}
        <div className='chat-message system-note'>
          {notes.map((note, index) => (
            <div key={`note-${index}`} className='message-note'>
              {note.content}
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {prettyDate && (
        <div className='message-date'>
          {prettyDate} • {prettyTime}
        </div>
      )}
      <div className={cn('chat-message', role)}>
        {label && <div className='message-label'>{label}</div>}
        <div className='message-bubble'>
          <div className='message-contents'>
            {message.role === MessageRole.USER ? (
              <>
                {message.attachment && <MessageAttachment attachment={message.attachment} />}
                {message.content}
              </>
            ) : (
              groupToolCalls(artifactSegmentsLast(segments)).map((segment, index) => {
                if (segment.type === 'text') {
                  return (
                    // rehype-raw passes embedded HTML through, then
                    // rehype-sanitize strips anything dangerous with the
                    // default schema. Matches bpm-frontend's parity for
                    // `<details>`, `<sub>`, `<sup>`, etc. without the XSS
                    // surface area of unsanitized HTML.
                    <ReactMarkdown
                      key={`text-${index}`}
                      remarkPlugins={AGENT_CHAT_REMARK_PLUGINS}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={AGENT_CHAT_MARKDOWN_COMPONENTS}
                    >
                      {segment.content}
                    </ReactMarkdown>
                  );
                }
                if (segment.type === 'artifact') {
                  return (
                    <ArtifactDownload key={`artifact-${segment.filename}`} segment={segment} />
                  );
                }
                if (segment.type === 'note') {
                  return (
                    <div key={`note-${index}`} className='message-note'>
                      {segment.content}
                    </div>
                  );
                }
                if (segment.type === 'tool_group') {
                  const [first] = segment.calls;
                  return (
                    <ToolCallGroup
                      key={`tool-group-${index}`}
                      name={first.title ?? humanizeToolName(first.name)}
                      argsList={segment.calls.map(call => (isInternal ? call.args : undefined))}
                    />
                  );
                }
                return (
                  <ToolCallChip
                    key={`tool-${index}`}
                    name={segment.title ?? humanizeToolName(segment.name)}
                    args={isInternal ? segment.args : undefined}
                  />
                );
              })
            )}
          </div>
        </div>
        <div className='message-footer'>
          <div className='message-time'>{prettyTime}</div>
          <div className='message-actions'>
            <CopyButton text={copyText} aria-label='Copy message' />
            {showFeedback && (
              <>
                <IconButton
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  size={Size.MD}
                  className={cn({ active: currentFeedback === Feedback.POSITIVE })}
                  onClick={() => onFeedbackClick(Feedback.POSITIVE)}
                  tooltip={{ text: 'Like' }}
                  aria-label='Like response'
                >
                  {currentFeedback === Feedback.POSITIVE ? (
                    <MdThumbUpAlt />
                  ) : (
                    <MdOutlineThumbUpAlt />
                  )}
                </IconButton>
                <IconButton
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  size={Size.MD}
                  className={cn({ active: currentFeedback === Feedback.NEGATIVE })}
                  onClick={() => onFeedbackClick(Feedback.NEGATIVE)}
                  tooltip={{ text: 'Dislike' }}
                  aria-label='Dislike response'
                >
                  {currentFeedback === Feedback.NEGATIVE ? (
                    <MdThumbDownAlt />
                  ) : (
                    <MdOutlineThumbDownAlt />
                  )}
                </IconButton>
              </>
            )}
          </div>
        </div>
        {showFeedback && currentFeedback === Feedback.NEGATIVE && (
          <FeedbackReasonForm selectedReason={currentReason} onSubmit={onReasonSubmit} />
        )}
      </div>
    </>
  );
};

interface FeedbackReasonFormProps {
  selectedReason: string | null;
  onSubmit: (reason: string) => void;
}

const FeedbackReasonForm = ({ selectedReason, onSubmit }: FeedbackReasonFormProps) => {
  const isOtherReason =
    selectedReason !== null && !NEGATIVE_FEEDBACK_OPTIONS.slice(0, -1).includes(selectedReason);
  const [showOtherInput, setShowOtherInput] = useState(isOtherReason);
  const [otherText, setOtherText] = useState(isOtherReason ? selectedReason : '');

  return (
    <div className='feedback-reason-form'>
      <div className='feedback-question'>Why wasn&apos;t this response helpful?</div>
      {NEGATIVE_FEEDBACK_OPTIONS.map(option => {
        const isOther = option === 'Other';
        const isSelected = isOther ? showOtherInput : selectedReason === option;
        return (
          <Radio
            key={option}
            id={`feedback-option-${option}`}
            label={option}
            checked={isSelected}
            onChange={() => {
              if (isOther) {
                setShowOtherInput(true);
              } else {
                setShowOtherInput(false);
                setOtherText('');
                onSubmit(option);
              }
            }}
          />
        );
      })}
      {showOtherInput && (
        <>
          <Form.Control
            placeholder='Tell us more...'
            value={otherText}
            onChange={e => setOtherText(e.target.value)}
          />
          <Button
            size={Size.SM}
            disabled={!otherText.trim()}
            onClick={() => onSubmit(`Other: ${otherText.trim()}`)}
          >
            Submit Feedback
          </Button>
        </>
      )}
    </div>
  );
};

interface MessageAttachmentProps {
  attachment: ChatAttachment;
}

const MessageAttachment = ({ attachment }: MessageAttachmentProps) => (
  <div className='message-attachment'>
    <MdInsertDriveFile />
    <span className='attachment-name'>{attachment.filename}</span>
    <span className='attachment-size'>{formatFileSize(attachment.size)}</span>
  </div>
);

interface ToolCallChipProps {
  name: string;
  // Raw arguments shown (formatted, copyable) on click; only passed for internal users.
  args?: Record<string, unknown>;
}

const ToolCallChip = ({ name, args }: ToolCallChipProps) => {
  const [expanded, setExpanded] = useState(false);

  const chipContent = (
    <>
      <MdBuild size={14} />
      <span className='tool-call-name'>{name}</span>
    </>
  );

  if (!args) return <div className='tool-call-chip'>{chipContent}</div>;

  // Display parses embedded JSON strings for readability; copy stays faithful to the real args.
  const formattedArgs = formatToolArgs(args);
  const rawArgs = JSON.stringify(args, null, 2);

  return (
    <>
      <InteractiveDiv
        className={cn('tool-call-chip', 'clickable')}
        aria-expanded={expanded}
        onClick={() => setExpanded(v => !v)}
      >
        {chipContent}
      </InteractiveDiv>
      {expanded && (
        <div className='tool-call-args'>
          <span className='tool-call-args-copy'>
            <CopyButton text={rawArgs} aria-label='Copy tool arguments' />
          </span>
          <pre>{formattedArgs}</pre>
        </div>
      )}
    </>
  );
};

interface ToolCallGroupProps {
  name: string;
  // One entry per call, in order; undefined when the viewer may not see that call's args.
  argsList: (Record<string, unknown> | undefined)[];
}

const ToolCallGroup = ({ name, argsList }: ToolCallGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  if (argsList.every(args => args === undefined)) {
    return (
      <div className='tool-call-chip'>
        <MdBuild size={14} />
        <span className='tool-call-name'>{name}</span>
        <span className='tool-call-count'>{argsList.length}</span>
      </div>
    );
  }

  return (
    <>
      <InteractiveDiv
        className={cn('tool-call-chip', 'clickable')}
        aria-expanded={expanded}
        onClick={() => setExpanded(v => !v)}
      >
        <MdBuild size={14} />
        <span className='tool-call-name'>{name}</span>
        <span className='tool-call-count'>{argsList.length}</span>
      </InteractiveDiv>
      {expanded && (
        <div className='tool-call-group-calls'>
          {argsList.map((args, index) => (
            <ToolCallChip key={`call-${index}`} name={`${name} #${index + 1}`} args={args} />
          ))}
        </div>
      )}
    </>
  );
};

interface ArtifactDownloadProps {
  segment: AgentArtifactSegment;
}

const ArtifactDownload = ({ segment }: ArtifactDownloadProps) => {
  if (!segment.url) {
    return (
      <Button
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        className='agent-artifact'
        disabled
        leadingElement={<MdOutlineFileDownload />}
      >
        {segment.filename} (link expired)
      </Button>
    );
  }
  return (
    <Button
      appearance={Appearance.OUTLINED}
      design={Design.NEUTRAL}
      size={Size.SM}
      className='agent-artifact'
      onClick={() => window.open(segment.url, '_blank')}
      leadingElement={<MdOutlineFileDownload />}
    >
      {segment.filename}
    </Button>
  );
};

interface PromptInputProps {
  sendPrompt: (message: string, attachment?: PendingAttachment) => void;
  onStop?: () => void;
  stopping?: boolean;
  submitting: boolean;
  draftMessage: string;
  setDraftMessage: Dispatch<SetStateAction<string>>;
  inputRef: MutableRefObject<HTMLTextAreaElement | null>;
  autoFocus: boolean;
  placeholder?: string;
  allowCsvAttachment: boolean;
}

const PromptInput = ({
  sendPrompt,
  onStop,
  stopping,
  submitting,
  draftMessage,
  setDraftMessage,
  inputRef,
  autoFocus,
  placeholder = 'Ask about your data...',
  allowCsvAttachment
}: PromptInputProps) => {
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  // The name of a file still being read. Send waits for it.
  const [readingFilename, setReadingFilename] = useState<string | null>(null);
  // Bumped on every pick or removal, so a read that finishes after a newer one is ignored.
  const latestReadRef = useRef(0);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus, inputRef]);

  const selectAttachment = (file: File) => {
    latestReadRef.current += 1;
    const read = latestReadRef.current;
    const isLatest = () => read === latestReadRef.current;
    const error = getCsvAttachmentError(file);
    setAttachmentError(error);
    setAttachment(null);
    setReadingFilename(error ? null : file.name);
    if (error) return;
    readCsvAttachment(file)
      .then(result => {
        if (isLatest()) {
          setAttachment(result);
        }
      })
      .catch(() => {
        if (isLatest()) {
          setAttachmentError("Couldn't read that file. Try attaching it again.");
        }
      })
      .finally(() => {
        if (isLatest()) {
          setReadingFilename(null);
        }
      });
  };

  const removeAttachment = () => {
    latestReadRef.current += 1;
    setAttachment(null);
    setAttachmentError(null);
    setReadingFilename(null);
  };

  const submit = () => {
    const trimmed = draftMessage.trim();
    if ((!trimmed && !attachment) || submitting || readingFilename) return;
    sendPrompt(trimmed, attachment ?? undefined);
    setDraftMessage('');
    removeAttachment();
  };

  return (
    <>
      {readingFilename && (
        <PendingAttachmentChip
          filename={readingFilename}
          detail='Reading…'
          onRemove={removeAttachment}
        />
      )}
      {attachment && (
        <PendingAttachmentChip
          filename={attachment.filename}
          detail={formatFileSize(attachment.size)}
          onRemove={removeAttachment}
        />
      )}
      {attachmentError && (
        <div className='attachment-error' role='alert'>
          {attachmentError}
        </div>
      )}
      <div className={cn('prompt-input', { 'with-attach': allowCsvAttachment })}>
        <Form.Control
          as='textarea'
          rows={3}
          placeholder={placeholder}
          value={draftMessage}
          onChange={e => setDraftMessage(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape' && submitting && onStop && !stopping) {
              e.preventDefault();
              onStop();
            }
          }}
          ref={inputRef}
        />
        {allowCsvAttachment && <AttachCsvButton disabled={submitting} onFile={selectAttachment} />}
        {submitting && onStop ? (
          <IconButton
            design={Design.NEUTRAL}
            className='submit-btn'
            loading={stopping}
            disabled={stopping}
            onClick={onStop}
            tooltip={{ text: stopping ? 'Stopping…' : 'Stop generating (Esc)' }}
            aria-label={stopping ? 'Stopping' : 'Stop generating'}
          >
            <MdStop />
          </IconButton>
        ) : (
          <IconButton
            className='submit-btn'
            disabled={Boolean(readingFilename) || (!draftMessage.trim() && !attachment)}
            loading={submitting}
            onClick={submit}
            aria-label='Send message'
          >
            <MdArrowUpward />
          </IconButton>
        )}
      </div>
    </>
  );
};

interface AttachCsvButtonProps {
  disabled: boolean;
  onFile: (file: File) => void;
}

const AttachCsvButton = ({ disabled, onFile }: AttachCsvButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.LG}
        className='attach-btn'
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
        tooltip={{ text: 'Attach a CSV' }}
        aria-label='Attach a CSV file'
      >
        <MdAttachFile />
      </IconButton>
      <input
        ref={fileInputRef}
        type='file'
        accept={CSV_ATTACHMENT_ACCEPT}
        className='hidden-file-input'
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (file) {
            onFile(file);
          }
          // Clear it so picking the same file again (e.g. after removing it) fires another change.
          e.target.value = '';
        }}
      />
    </>
  );
};

interface PendingAttachmentChipProps {
  filename: string;
  // The size once read, or a reading indicator before then.
  detail: string;
  onRemove: () => void;
}

const PendingAttachmentChip = ({ filename, detail, onRemove }: PendingAttachmentChipProps) => (
  <div className='pending-attachment'>
    <MdInsertDriveFile />
    <span className='attachment-name'>{filename}</span>
    <span className='attachment-size'>{detail}</span>
    <IconButton
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      onClick={onRemove}
      aria-label='Remove attachment'
    >
      <MdClose />
    </IconButton>
  </div>
);

interface SuggestedPromptsProps {
  prompts: string[];
  onSelect: (prompt: string) => void;
}

const SuggestedPrompts = ({ prompts, onSelect }: SuggestedPromptsProps) => (
  <div className='suggested-prompts'>
    {prompts.map(prompt => (
      <InteractiveDiv key={prompt} className='suggested-prompt' onClick={() => onSelect(prompt)}>
        <MdOutlineAutoAwesome className='suggested-prompt-icon' />
        <span className='suggested-prompt-text'>{prompt}</span>
      </InteractiveDiv>
    ))}
  </div>
);

interface PromptTipProps {
  tip: string;
  onSelect: () => void;
}

const PromptTip = ({ tip, onSelect }: PromptTipProps) => (
  <InteractiveDiv className='prompt-tip' onClick={onSelect}>
    <MdOutlineAutoAwesome className='prompt-tip-icon' />
    <span className='prompt-tip-label'>Tip</span>
    <span>Ask: {tip}</span>
  </InteractiveDiv>
);

const exportChat = (messages: AgentMessage[]): void => {
  const lines: string[] = [`# Agent Chat\nExported: ${format(new Date(), 'MMMM d, yyyy')}\n`];
  for (const msg of messages) {
    if (msg.role === MessageRole.USER) {
      const attached = msg.attachment ? `\n\n_Attached: ${msg.attachment.filename}_` : '';
      lines.push(`---\n\n**You:** ${msg.content}${attached}`);
    } else {
      const text = msg.segments
        .filter((s): s is AgentTextSegment => s.type === 'text')
        .map(s => s.content)
        .join('\n\n');
      const artifacts = msg.segments
        .filter((s): s is AgentArtifactSegment => s.type === 'artifact')
        .map(s => (s.url ? `[Download: ${s.filename}](${s.url})` : `${s.filename} (link expired)`))
        .join('\n');
      const body = [text, artifacts].filter(Boolean).join('\n\n');
      if (body) {
        lines.push(`---\n\n**Agent:** ${body}`);
      }
    }
  }
  const blob = new Blob([lines.join('\n\n')], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `agent-chat-${format(new Date(), 'yyyy-MM-dd')}.md`;
  a.click();
  URL.revokeObjectURL(url);
};

interface PrivacyFooterProps {
  messages: AgentMessage[];
}

const PrivacyFooter = ({ messages }: PrivacyFooterProps) => {
  const hasUserMessages = messages.some(m => m.role === MessageRole.USER);
  return (
    <div className='privacy-footer'>
      {hasUserMessages && (
        <Button
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          size={Size.SM}
          onClick={() => exportChat(messages)}
          leadingElement={<MdShare />}
          tooltip={{ text: 'Export chat as Markdown' }}
        >
          Export
        </Button>
      )}
      <span>AI generated content. Please verify for accuracy.</span>
      <Button
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={() => window.open(PRIVACY_POLICY_URL, '_blank')}
      >
        Privacy Policy
      </Button>
    </div>
  );
};
